import React, { Fragment } from 'react'

import { Dialog, Transition } from '@headlessui/react'
import { HiOutlineXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import IconButton from '@/components/common/IconButton'

interface Drawer {
  open: boolean
  toggleDrawer: () => void
  children: React.ReactNode
  onClose?: () => void
  /** Which edge the panel slides from. Defaults to the historical left. */
  side?: 'left' | 'right'
  /** Panel width. Defaults to the historical narrow (20rem) panel. */
  width?: 'narrow' | 'wide'
  /**
   * Classes for the panel's content box, merged over its default padding.
   * Pass `p-0` when the drawer body owns its own padding — e.g. so a scroll
   * area can run edge to edge and its sticky headers can pin to the panel top.
   */
  contentClassName?: string
}

const Drawer: React.FC<Drawer> = ({
  open,
  toggleDrawer,
  children,
  onClose = () => {},
  side = 'left',
  width = 'narrow',
  contentClassName,
}) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Dialog.Overlay
          className={twMerge(
            'fixed transition-opacity duration-300 inset-0 bg-black',
            open ? 'opacity-50' : 'opacity-0',
          )}
          onClick={toggleDrawer}
        />
        <div className="fixed overflow-hidden">
          <div
            className={twMerge(
              'absolute inset-0 overflow-hidden',
              side === 'left' ? 'left-0' : 'right-0',
            )}
          >
            <div
              className={twMerge(
                'pointer-events-none fixed inset-y-0 flex',
                width === 'narrow' ? 'max-w-80' : 'max-w-md',
                side === 'right' && 'right-0',
              )}
            >
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom={
                  side === 'left' ? '-translate-x-full' : 'translate-x-full'
                }
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo={
                  side === 'left' ? '-translate-x-full' : 'translate-x-full'
                }
              >
                <Dialog.Panel
                  className={twMerge(
                    'relative pointer-events-auto w-screen h-full bg-white dark:bg-primary-900',
                    width === 'narrow' ? 'max-w-xs' : 'max-w-md',
                  )}
                >
                  {open && side === 'left' && (
                    <div className="flex items-center justify-center absolute -right-9 top-2">
                      <IconButton
                        className="hover:bg-transparent dark:hover:bg-transparent hover:opacity-70"
                        onClick={toggleDrawer}
                      >
                        <HiOutlineXMark className="h-5 w-5 text-primary-100" />
                      </IconButton>
                    </div>
                  )}
                  <div
                    className={twMerge(
                      'px-4 py-6 h-full overflow-y-auto',
                      contentClassName,
                    )}
                  >
                    {children}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default Drawer
